import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@base/components/ui/tooltip';
import { Button } from '@basic';
import { UIPaths } from '@consts';
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon, ComboIcon } from '@icons';
import { GoalModel, GoalSubscriptionModel, GoalType, GoalVisibilityStatus } from '@models';
import { toProperCase } from '@nlp';
import { UserAvatar } from '@shared';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { BasicGoalSubscriptionModel, GoalSubscriptionStatus } from 'lib/models/shared';

import GoalProgressBar from '@components/progress/GoalProgressBar';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GoalActions from './GoalActions';
import GoalAuthor from './GoalAuthor';
import GoalCardOrderedLabel from './GoalCardOrderedLabel';
import GoalLogo from './GoalLogo';
import GoalSubscriptionStatusChip from './GoalSubscriptionStatusChip';
import ReleaseAfterChip from './ReleaseAfterChip';
export default function GoalCard({
  goal,
  showActions,
  goalSubscription,
  customLink,
  onShowChildGoals,
  hideChevron = false,
  orderedLabel = null,
  className = ''
}: {
  goal: GoalModel;
  showActions: {
    versionType?: boolean;
    deadline?: boolean;
    priority?: boolean;
    like?: boolean;
    conversation?: boolean;
    share?: boolean;
    more?: boolean;
    edit?: boolean;
  };
  goalSubscription?: GoalSubscriptionModel | BasicGoalSubscriptionModel | null;
  customLink?: string;
  onShowChildGoals?: (showChildGoals: boolean) => void;
  hideChevron?: boolean;
  orderedLabel?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [showChildGoals, setShowChildGoals] = useState(false);
  const subscriptionProgress = goalSubscription?.progress;

  /**
   * TITLE
   */
  let titleClasses = 'H6 line-clamp-3';
  if (goalSubscription && goalSubscription.status === GoalSubscriptionStatus.PAUSED) {
    titleClasses = classNames(titleClasses, setupForDark('text-tertiary'));
  } else {
    titleClasses = classNames(titleClasses, setupForDark('text-primary'));
  }

  let titleEl = (
    <div className='flex flex-col gap-xxs justify-center items-start'>
      <div data-testid={`goalCard-title-goal-${goal.entityId}`} className={titleClasses}>
        {toProperCase(goal.title || t('goal.defaultTitle'))}
      </div>
    </div>
  );

  const onMapButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(UIPaths.goalGraph(goal.entityId));
  };

  const onCardClick = () => {
    router.push(customLink || UIPaths.goal(goal.entityId, goal.versionType));
  };

  return (
    <div
      data-testid={`goalCard-goal-${goal.entityId}`}
      className='w-full cursor-pointer'
      onClick={onCardClick}
    >
      <div
        className={classNames(
          'flex flex-row rounded-md w-full',
          className,
          goal.visibilityStatus !== GoalVisibilityStatus.WIP &&
            classNames(setupForDark('bg-secondary border-primary'), 'border-[1px]'),
          goal.visibilityStatus === GoalVisibilityStatus.WIP &&
            classNames(setupForDark('border-dark'), 'border-dashed', 'border-[1px]')
        )}
      >
        {orderedLabel && <GoalCardOrderedLabel orderedLabel={orderedLabel} />}
        <div className={classNames('flex flex-col justify-center items-start gap-sm w-full p-xl')}>
          <div className='flex flex-row justify-between items-center w-full gap-sm'>
            <GoalLogo logoSvg={goal.logoSvg} size={32} status={goal?.subscription?.status} />
            <div className='flex flex-row gap-xxs items-center justify-center'>
              {goalSubscription && <GoalSubscriptionStatusChip status={goalSubscription.status} />}
              {goal.isChildGoal && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger>
                      <ComboIcon />
                    </TooltipTrigger>
                    <TooltipContent>{t('goal.tooltipAboutParentGoal')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {goal.visibilityStatus === GoalVisibilityStatus.WIP && (
              <ReleaseAfterChip releaseDate={goal.releaseDate} />
            )}
            <GoalActions goal={goal} showActions={showActions} />
          </div>
          <GoalAuthor goal={goal} />
          {titleEl}
          {goalSubscription && (
            <GoalProgressBar goalEntityId={goal.entityId} progress={subscriptionProgress} />
          )}
          {/* TEMPORARY DEBUGGING TRICK TO ALLOW US ( MENTORS ) TO SEE QUICKLY IDENTIFY USERS */}
          <div className={classNames('flex flex-row gap-xxs items-baseline')}>
            {(goalSubscription as GoalSubscriptionModel)?.conversation?.participants
              .filter((participant) => !participant.isProducer && !participant?.user?.isMe)
              .map((participant, i) => (
                <div key={participant.id} className='flex flex-row gap-xxs items-center'>
                  <UserAvatar
                    pictureUrl={participant.user?.pictureUrl}
                    size={36}
                    isProducer={participant.isProducer}
                    isChatbot={false}
                    isMaster={participant.isMaster}
                  />
                  <div className='P3M'>{participant.user?.name}</div>
                </div>
              ))}
          </div>
          {goal.type === GoalType.COMBO && (
            <div className='flex flex-row w-full justify-between items-center'>
              <Button type='primary' size='medium' onClick={onMapButtonClick}>
                {t('domains.goals.mapButtonText')}
                <ArrowRightIcon size={16} />
              </Button>
              {!showChildGoals && !hideChevron && (
                <ChevronDownIcon
                  size={24}
                  className={classNames(setupForDark('text-primary'))}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowChildGoals(true);
                    onShowChildGoals?.(true);
                  }}
                />
              )}
              {showChildGoals && (
                <ChevronUpIcon
                  size={24}
                  className={classNames(setupForDark('text-primary'))}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowChildGoals(false);
                    onShowChildGoals?.(false);
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
